import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { AUTH_QUERY_KEY } from '../auth/useAuth';
import { apiFetch, createAvatar, updateAvatar, activateAvatar } from '../../lib/api';
import type { SavedAvatarData } from '../../lib/api';
import type { AvatarConfig } from './avatarTypes';
import { defaultAvatarConfig, WIZARD_STEPS } from './avatarTypes';
import {
  BASE_SPRITES,
  EYE_PARTS,
  MOUTH_PARTS,
  ACCESSORY_PARTS,
  BACKGROUND_PATTERNS,
  PALETTE_PRESETS,
  SPRITES_BY_ID,
} from './avatarData';
import AvatarCanvas from './AvatarCanvas';
import type { AvatarCanvasHandle } from './AvatarCanvas';
import { useAvatarAnimations } from './avatarAnimations';
import StepWizard from './StepWizard';
import PartPicker from './PartPicker';
import ColorPicker from './ColorPicker';
import PixelEditor from './PixelEditor';
import type { PixelTool } from './PixelEditor';
import InfoPanel from './InfoPanel';
import { exportPNG, copyShareLink, decodeShareLink } from './avatarExport';
import './avatar.css';

export default function AvatarCreatorPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // URL params for edit/fork/share modes
  const sharedParam = searchParams.get('v');
  const editId = searchParams.get('edit');
  const forkId = searchParams.get('fork');
  const sharedConfig = useMemo(
    () => (sharedParam ? decodeShareLink(sharedParam) : null),
    [sharedParam],
  );
  const [readOnly, setReadOnly] = useState(!!sharedConfig);
  const [editingAvatarId, setEditingAvatarId] = useState<number | null>(
    editId ? Number(editId) : null,
  );

  const [config, setConfig] = useState<AvatarConfig>(
    () => sharedConfig ?? defaultAvatarConfig(),
  );

  // Load config from server for edit/fork modes
  useEffect(() => {
    const loadId = editId || forkId;
    if (!loadId) return;
    apiFetch<SavedAvatarData>(`/api/avatars/${loadId}/`)
      .then((data) => {
        setConfig(data.config as unknown as AvatarConfig);
        if (editId) setEditingAvatarId(data.id);
      })
      .catch(() => toast.error('Failed to load avatar'));
  }, [editId, forkId]);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [hoverConfig, setHoverConfig] = useState<AvatarConfig | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [scrambling, setScrambling] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pixel editor state
  const [pixelEditing, setPixelEditing] = useState(false);
  const [pixelTool, setPixelTool] = useState<PixelTool>('paint');
  const [pixelColor, setPixelColor] = useState('#f38ba8');

  const canvasHandleRef = useRef<AvatarCanvasHandle>(null);
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);

  // Get the actual canvas element from the handle
  useEffect(() => {
    canvasElementRef.current = canvasHandleRef.current?.getCanvas() ?? null;
  });

  const displayConfig = hoverConfig ?? config;
  const animationOffsets = useAvatarAnimations(canvasElementRef, displayConfig, !scrambling && !pixelEditing);

  // ── Config updaters ─────────────────────────────────────────────────

  const updateConfig = useCallback((patch: Partial<AvatarConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  const markStepDone = useCallback((step: number) => {
    setCompletedSteps((prev) => new Set(prev).add(step));
  }, []);

  // ── Step navigation ─────────────────────────────────────────────────

  const goToStep = useCallback((step: number) => {
    markStepDone(currentStep);
    setCurrentStep(step);
  }, [currentStep, markStepDone]);

  const goBack = useCallback(() => {
    if (currentStep > 0) goToStep(currentStep - 1);
  }, [currentStep, goToStep]);

  const goNext = useCallback(() => {
    if (currentStep < WIZARD_STEPS.length - 1) goToStep(currentStep + 1);
  }, [currentStep, goToStep]);

  // ── Hover preview ───────────────────────────────────────────────────

  const handleHover = useCallback(
    (id: string | null, field: keyof AvatarConfig) => {
      if (id === null) {
        setHoverConfig(null);
      } else {
        setHoverConfig({ ...config, [field]: id } as AvatarConfig);
      }
    },
    [config],
  );

  const handleBaseHover = useCallback(
    (id: string | null) => {
      if (id === null) {
        setHoverConfig(null);
      } else {
        const sprite = SPRITES_BY_ID[id];
        if (sprite) {
          setHoverConfig({
            ...config,
            entityType: id,
            entityCategory: sprite.category,
          });
        }
      }
    },
    [config],
  );

  // ── Selection handlers ──────────────────────────────────────────────

  const selectEntity = useCallback(
    (id: string) => {
      const sprite = SPRITES_BY_ID[id];
      if (sprite) {
        updateConfig({ entityType: id, entityCategory: sprite.category });
      }
    },
    [updateConfig],
  );

  // ── Pixel editing handlers ──────────────────────────────────────────

  const handlePixelClick = useCallback(
    (row: number, col: number) => {
      const key = `${row},${col}`;
      if (pixelTool === 'erase') {
        setConfig((prev) => {
          if (!prev.pixelOverrides?.[key]) return prev;
          const next = { ...prev.pixelOverrides };
          delete next[key];
          return { ...prev, pixelOverrides: Object.keys(next).length > 0 ? next : undefined };
        });
      } else {
        setConfig((prev) => ({
          ...prev,
          pixelOverrides: { ...prev.pixelOverrides, [key]: pixelColor },
        }));
      }
    },
    [pixelTool, pixelColor],
  );

  const handleClearPixels = useCallback(() => {
    setConfig((prev) => {
      const { pixelOverrides: _, ...rest } = prev;
      return rest as AvatarConfig;
    });
  }, []);

  const handleAddCustomColor = useCallback((color: string) => {
    setConfig((prev) => ({
      ...prev,
      customColors: [...(prev.customColors ?? []), color],
    }));
  }, []);

  const handleRemoveCustomColor = useCallback((index: number) => {
    setConfig((prev) => {
      const next = [...(prev.customColors ?? [])];
      next.splice(index, 1);
      return { ...prev, customColors: next.length > 0 ? next : undefined };
    });
  }, []);

  // ── Randomize ───────────────────────────────────────────────────────

  const randomize = useCallback(() => {
    setScrambling(true);

    let frame = 0;
    const maxFrames = 8;
    const interval = setInterval(() => {
      frame++;
      if (frame >= maxFrames) {
        clearInterval(interval);
        const rBase = BASE_SPRITES[Math.floor(Math.random() * BASE_SPRITES.length)];
        const rEyes = EYE_PARTS[Math.floor(Math.random() * EYE_PARTS.length)];
        const rMouth = MOUTH_PARTS[Math.floor(Math.random() * MOUTH_PARTS.length)];
        const rAcc = ACCESSORY_PARTS[Math.floor(Math.random() * ACCESSORY_PARTS.length)];
        const rBg = BACKGROUND_PATTERNS[Math.floor(Math.random() * BACKGROUND_PATTERNS.length)];
        const rPalette = PALETTE_PRESETS[Math.floor(Math.random() * PALETTE_PRESETS.length)];

        setConfig({
          entityType: rBase.id,
          entityCategory: rBase.category,
          eyes: rEyes.id,
          mouth: rMouth.id,
          accessory: rAcc.id,
          background: rBg.id,
          palette: { ...rPalette.colors },
          gridSize: 14,
          name: `threat_${rBase.id}_${String(Math.floor(Math.random() * 100)).padStart(2, '0')}`,
        });
        setScrambling(false);
      } else {
        const rBase = BASE_SPRITES[Math.floor(Math.random() * BASE_SPRITES.length)];
        setConfig((prev) => ({ ...prev, entityType: rBase.id, entityCategory: rBase.category }));
      }
    }, 60);
  }, []);

  // ── Export handlers ─────────────────────────────────────────────────

  const handleExportPNG = useCallback(async () => {
    if (!canvasHandleRef.current) return;
    await exportPNG(canvasHandleRef.current, config.name);
    toast.success('PNG downloaded!');
  }, [config.name]);

  const handleShare = useCallback(async () => {
    await copyShareLink(config);
    toast.success('Link copied!');
  }, [config]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      if (editingAvatarId) {
        // Update existing avatar
        await updateAvatar(editingAvatarId, {
          name: config.name,
          config: config as unknown as Record<string, unknown>,
        });
        qc.invalidateQueries({ queryKey: ['avatars'] });
        qc.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
        toast.success('Avatar updated!');
      } else {
        // Create new avatar
        const saved = await createAvatar({
          name: config.name,
          config: config as unknown as Record<string, unknown>,
        });
        setEditingAvatarId(saved.id);
        qc.invalidateQueries({ queryKey: ['avatars'] });

        // Offer to activate
        if (confirm('Avatar saved! Set as your active avatar?')) {
          await activateAvatar(saved.id);
          qc.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
          toast.success('Avatar saved and activated!');
        } else {
          toast.success('Avatar saved to collection!');
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save avatar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [config, editingAvatarId, qc]);

  // ── Read-only mode actions ──────────────────────────────────────────

  const enterEditMode = useCallback(() => {
    setReadOnly(false);
    setSearchParams({});
  }, [setSearchParams]);

  const useAsStartingPoint = useCallback(() => {
    setReadOnly(false);
    setSearchParams({});
  }, [setSearchParams]);

  // ── Step content renderer ───────────────────────────────────────────

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Base entity
        return (
          <PartPicker
            options={BASE_SPRITES.map((s) => ({
              id: s.id,
              name: s.name,
              grid: s.grid,
              category: s.category,
            }))}
            selected={config.entityType}
            onSelect={selectEntity}
            onHover={handleBaseHover}
            palette={config.palette}
            previewColorIndex={1}
          />
        );
      case 1: // Eyes
        return (
          <PartPicker
            options={EYE_PARTS}
            selected={config.eyes}
            onSelect={(id) => updateConfig({ eyes: id })}
            onHover={(id) => handleHover(id, 'eyes')}
            palette={config.palette}
            previewColorIndex={3}
          />
        );
      case 2: // Mouth
        return (
          <PartPicker
            options={MOUTH_PARTS}
            selected={config.mouth}
            onSelect={(id) => updateConfig({ mouth: id })}
            onHover={(id) => handleHover(id, 'mouth')}
            palette={config.palette}
            previewColorIndex={4}
          />
        );
      case 3: // Accessory
        return (
          <PartPicker
            options={ACCESSORY_PARTS}
            selected={config.accessory}
            onSelect={(id) => updateConfig({ accessory: id })}
            onHover={(id) => handleHover(id, 'accessory')}
            palette={config.palette}
            previewColorIndex={5}
          />
        );
      case 4: // Background
        return (
          <PartPicker
            options={BACKGROUND_PATTERNS}
            selected={config.background}
            onSelect={(id) => updateConfig({ background: id })}
            onHover={(id) => handleHover(id, 'background')}
            palette={config.palette}
            previewColorIndex={1}
          />
        );
      case 5: // Colors
        return (
          <ColorPicker
            palette={config.palette}
            onChange={(palette) => updateConfig({ palette })}
          />
        );
      default:
        return null;
    }
  };

  // ── Keyboard step navigation ────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (readOnly) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        goBack();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [readOnly, goNext, goBack]);

  return (
    <div className="malpedia-avatar-page">
      {/* Top Bar */}
      <div className="malpedia-avatar-topbar">
        <div className="malpedia-avatar-topbar-left">
          <span className="malpedia-avatar-topbar-icon">&#x1F9E0;</span>
          <h1 className="malpedia-avatar-topbar-title">Threat Avatar Creator</h1>
        </div>
        <div className="malpedia-avatar-topbar-right">
          {!readOnly && (
            <>
              <button
                className={`malpedia-avatar-btn${pixelEditing ? ' malpedia-avatar-btn--primary' : ''}`}
                onClick={() => setPixelEditing((v) => !v)}
                title="Edit individual pixels"
              >
                &#x270F;&#xFE0F; {pixelEditing ? 'Exit Pixels' : 'Edit Pixels'}
              </button>
              <button className="malpedia-avatar-btn" onClick={randomize} disabled={scrambling}>
                Randomize
              </button>
              <button className="malpedia-avatar-btn" onClick={handleExportPNG}>
                PNG
              </button>
              <button className="malpedia-avatar-btn" onClick={handleShare}>
                Share
              </button>
              <button
                className="malpedia-avatar-btn malpedia-avatar-btn--primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving\u2026' : 'Save'}
              </button>
            </>
          )}
          {readOnly && (
            <>
              <button className="malpedia-avatar-btn malpedia-avatar-btn--primary" onClick={enterEditMode}>
                Make your own
              </button>
              <button className="malpedia-avatar-btn" onClick={useAsStartingPoint}>
                Use as starting point
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="malpedia-avatar-main">
        {/* Canvas Area */}
        <div className="malpedia-avatar-canvas-area">
          <div className="malpedia-avatar-canvas-wrapper">
            <AvatarCanvas
              ref={canvasHandleRef}
              config={displayConfig}
              pixelScale={pixelEditing ? 24 : 16}
              animationOffsets={animationOffsets}
              className="malpedia-avatar-canvas"
              onPixelClick={pixelEditing ? handlePixelClick : undefined}
              pixelEditing={pixelEditing}
            />
          </div>
          {/* Editable Name */}
          <div className="malpedia-avatar-name-row">
            {editingName ? (
              <input
                className="malpedia-avatar-name-input"
                value={config.name}
                onChange={(e) => updateConfig({ name: e.target.value })}
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => { if (e.key === 'Enter') setEditingName(false); }}
                autoFocus
              />
            ) : (
              <button
                className="malpedia-avatar-name-display"
                onClick={() => !readOnly && setEditingName(true)}
                disabled={readOnly}
              >
                {config.name} {!readOnly && '\u270F\uFE0F'}
              </button>
            )}
          </div>
        </div>

        {/* Pixel Editor Panel (replaces info panel when active) */}
        {pixelEditing ? (
          <div className="malpedia-avatar-info-area">
            <PixelEditor
              palette={config.palette}
              customColors={config.customColors ?? []}
              pixelOverrides={config.pixelOverrides ?? {}}
              tool={pixelTool}
              selectedColor={pixelColor}
              onToolChange={setPixelTool}
              onColorChange={setPixelColor}
              onClearAll={handleClearPixels}
              onAddCustomColor={handleAddCustomColor}
              onRemoveCustomColor={handleRemoveCustomColor}
              onClose={() => setPixelEditing(false)}
            />
          </div>
        ) : (
          <div className="malpedia-avatar-info-area">
            <InfoPanel config={displayConfig} />
          </div>
        )}
      </div>

      {/* Step Wizard (only in edit mode, hidden during pixel editing) */}
      {!readOnly && !pixelEditing && (
        <StepWizard
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={goToStep}
          onBack={goBack}
          onNext={goNext}
        >
          {renderStepContent()}
        </StepWizard>
      )}
    </div>
  );
}
